"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Menu, Phone, X } from "lucide-react";
import { Container } from "@/components/ui/container";
import { ButtonLink } from "@/components/ui/button";
import { Logo } from "@/components/navigation/logo";
import { mainNavigation, primaryCta } from "@/config/navigation";
import { site } from "@/config/site";
import { cn } from "@/lib/utils";

export function SiteHeader() {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      className={cn(
        "sticky top-0 z-50 border-b bg-background/85 backdrop-blur transition-shadow duration-300",
        scrolled ? "border-border shadow-soft" : "border-transparent",
      )}
    >
      <Container size="wide" className="flex min-h-18 items-center justify-between gap-4">
        <Logo priority />

        <nav aria-label="Hlavní navigace" className="hidden xl:block">
          <ul className="flex items-center gap-1">
            {mainNavigation.map((item) => {
              const active =
                item.href === "/" ? pathname === "/" : pathname.startsWith(item.href.split("#")[0]);
              return (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    aria-current={active ? "page" : undefined}
                    className={cn(
                      "inline-flex h-9 items-center whitespace-nowrap rounded-md px-3 text-sm font-medium leading-none transition-colors",
                      active
                        ? "text-brand-700"
                        : "text-ink-700 hover:bg-brand-50 hover:text-brand-700",
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${site.phone.replace(/\s/g, "")}`}
            className="hidden h-9 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm font-medium text-ink-700 transition-colors hover:text-brand-700 2xl:inline-flex"
          >
            <Phone className="size-4" aria-hidden />
            {site.phone}
          </a>
          <ButtonLink
            href={primaryCta.href}
            size="sm"
            className="hidden whitespace-nowrap sm:inline-flex"
          >
            {primaryCta.label}
          </ButtonLink>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Zavřít menu" : "Otevřít menu"}
            className="inline-grid size-11 place-items-center rounded-md border border-border text-ink-900 transition-colors hover:bg-sand-50 xl:hidden"
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>
      </Container>

      {open ? (
        <div id="mobile-menu" className="border-t border-border bg-background xl:hidden">
          <Container size="wide" className="py-4">
            <nav aria-label="Mobilní navigace">
              <ul className="flex flex-col divide-y divide-border">
                {mainNavigation.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="flex flex-col gap-0.5 py-3 text-base font-medium text-ink-900"
                    >
                      {item.label}
                      {item.description ? (
                        <span className="text-sm font-normal text-ink-400">{item.description}</span>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-5 flex flex-col gap-3">
              <ButtonLink href={primaryCta.href} size="md" onClick={() => setOpen(false)}>
                {primaryCta.label}
              </ButtonLink>
              <a
                href={`tel:${site.phone.replace(/\s/g, "")}`}
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-3 text-sm font-medium text-ink-900"
              >
                <Phone className="size-4" aria-hidden />
                {site.phone}
              </a>
            </div>
          </Container>
        </div>
      ) : null}
    </header>
  );
}
